/**
 * Project memory hosted by AIOE (mju-ai-workbench docs/plans/2026-09-project-model-end-to-end.md,
 * R5 M1): the index of record for an enterprise project. The workbench keeps extracting per
 * repository (on whichever desktop or node holds the clone) and pushes labelled deltas; AIOE
 * stores the items, serves every read through the reader's entitlement (clearance and
 * compartments, `memory.ts`), and audits reads by label, never content.
 *
 *   POST  /projects/v1/:id/memory/deltas           { rootId, revision, full, done, upserts, removes } → { accepted, removed, seq }
 *   POST  /projects/v1/:id/memory/query            { question, scope?, rootId?, limit? } → { answers, labelsServed, itemsFiltered }
 *   POST  /projects/v1/:id/memory/explain          { concept, scope?, rootId? } → { item, neighbours, communities }
 *   POST  /projects/v1/:id/memory/path             { from, to, scope? } → { path }
 *   GET   /projects/v1/:id/memory/graph?scope=&rootId=&since=<seq>&limit=   { seq, items, removed }
 *   GET   /projects/v1/:id/memory/status           items by kind and repository, revision and last ingest per repository
 *   POST  /projects/v1/:id/memory/items            { kind: note|lesson|decision, rootId?, data, label? } → the item
 *   PATCH /projects/v1/:id/memory/items/:iid       { label, reason } → the item (a person, never an agent)
 *   POST  /projects/v1/:id/memory/promote          owners: { itemIds[] | all, label? } → copies into the organisation's index
 *   PUT   /projects/v1/:id/memory/mirror           owners: { enabled, scope } → every later delta is mirrored too
 *
 * The organisation's own index (M3) has the same routes under /memory/v1 (deltas, query, explain,
 * path, graph, status, items) with projectId "" in the store: anyone signed in reads it through
 * their tenant entitlement; roles granted `memory:write` (and administrators) write to it.
 * `POST /memory/v1/query { projects: "mine" }` also searches every project the person can read.
 *
 * Items are keyed by project; `rootId` is the repository they came from ("" for project-level
 * items). `seq` is the project's board sequence, like tasks and notifications, so `since` reads
 * see removals as tombstones. A full replacement is scoped to (rootId, revision): the calls of
 * one revision upsert, and the one with `done: true` tombstones every live item of that
 * repository that the revision did not send.
 *
 * Meaning, history and proposals (M1–M3, D326–D328, mju-ai-workbench docs/plans/2026-10-memory-improvements.md) are
 * additive: vectors travel as f16 base64 with the digest of the text they came from, a read of the past names its
 * point with `asOf`, an agent's write may land as a proposal, and `…/memory/status` says which of these a home
 * offers (`capabilities`). The digests are computed here, the same on every side (projectMemory.fixture.ts).
 * Mirrored byte for byte by the workbench's shared/schemas/projectMemory.ts (and the fixture beside it).
 */
import { z } from "zod";
import { compartmentIdSchema, sensitivitySchema } from "./memory.js";
export const MEMORY_ITEM_KINDS = ["node", "edge", "community", "note", "lesson", "decision", "artifact", "knowledge"];
export const memoryItemKindSchema = z.enum(MEMORY_ITEM_KINDS);
export const memoryScopeSchema = z.enum(["repo", "knowledge"]);
const idSchema = z.string().min(1).max(300);
const rootIdSchema = z.string().max(200).regex(/^[A-Za-z0-9_.:-]*$/, "letters, digits and _ . : -");
/** Where an item came from: the file (or vault note) in which repository, at which commit, by whom. */
export const memoryProvenanceSchema = z.object({
    source: z.string().max(2000),
    commit: z.string().max(100).optional(),
    rootId: rootIdSchema.default(""),
    principal: z.string().max(200).optional(),
    actor: z.string().max(200).optional(),
    taskId: z.string().max(100).optional(),
    /** The project an item in the organisation's index was promoted or mirrored from. */
    project: z.string().max(200).optional(),
    at: z.string(),
});
export const memoryItemLabelSchema = z.object({
    sensitivity: sensitivitySchema,
    compartments: z.array(compartmentIdSchema).max(64).default([]),
    provenance: memoryProvenanceSchema,
    /** Set by a person's relabel: the next extraction push never lowers the label below it (max sensitivity, union of compartments). */
    pinned: z.boolean().optional(),
});
/** As the workbench sends it: the sensitivity may be left out (the project's default then applies, fail closed). */
export const memoryItemLabelInputSchema = memoryItemLabelSchema.extend({ sensitivity: sensitivitySchema.optional() });
export const memoryItemSchema = z.object({
    id: idSchema,
    projectId: z.string(),
    kind: memoryItemKindSchema,
    rootId: rootIdSchema,
    scope: memoryScopeSchema,
    label: memoryItemLabelSchema,
    /** node { name, type, summary?, sourceFile?, line? }; edge { from, to, relation, weight? }; community { name, members[], summary? }; note/lesson/decision { title, text, tags[] }; artifact { name, type?, summary?, source? }; knowledge: memoryKnowledge.ts. */
    data: z.record(z.string(), z.unknown()),
    version: z.number().int().positive(),
    seq: z.number().int().nonnegative(),
    /** The extractor's graph hash the item was last sent in. */
    revision: z.string().max(200).optional(),
    updatedAt: z.string(),
    /** A tombstone: the item was removed, and `since` reads say so. */
    deletedAt: z.string().optional(),
});
// ---- Finding by meaning (M1, D326; mju-ai-workbench docs/plans/2026-10-memory-improvements.md) ----------------
//
// The writing host embeds; the home never runs a model. One model per home (the organisation: the policy clause
// `memory.embeddingModel`), named in `…/memory/status`; a vector from another model is never mixed into an index.
/** A model memory can be embedded with. The id names the weights, not the runtime: the same id from llama.cpp, Ollama or LM Studio is the same model. */
export const embeddingModelSchema = z.object({
    id: z.string().regex(/^[a-z0-9][a-z0-9._-]{1,79}$/),
    dim: z.number().int().min(64).max(4000),
    /** Where it can run. A local model may be run by any of these; a hosted one only by its provider. */
    runs: z.array(z.enum(["local-runner", "ollama", "lm-studio", "hosted"])).min(1),
    /** The text put in front of a document and of a question (nomic-embed and e5 need them; others ""). */
    prefixes: z.object({ document: z.string().max(64), query: z.string().max(64) }),
    /** Ollama's tag. */
    ollama: z.string().optional(),
    /** The GGUF the bundled runner fetches. */
    gguf: z.object({ file: z.string(), sha256: z.string().length(64), bytes: z.number().int() }).optional(),
    /** The hosted provider and model, and the dimensions asked for when the model can shorten its vectors. */
    hosted: z.object({ provider: z.string(), model: z.string(), dimensions: z.number().int().optional() }).optional(),
    /** Below this cosine similarity an item never enters an answer by meaning alone: models' scales differ (unrelated short texts score about 0.5 with nomic). */
    minSimilarity: z.number().min(0).max(1).optional(),
});
/** The catalogue both sides agree on. The first is the default for every home that names none. */
export const EMBEDDING_MODELS = [
    {
        id: "nomic-embed-text-v1.5",
        dim: 768,
        runs: ["local-runner", "ollama", "lm-studio"],
        prefixes: { document: "search_document: ", query: "search_query: " },
        ollama: "nomic-embed-text:v1.5",
        gguf: { file: "nomic-embed-text-v1.5.Q8_0.gguf", sha256: "3e24342164b3d94991ba9692fdc0dd08e3fd7362e0aacc396a9a5c54a544c3b7", bytes: 146146432 },
        minSimilarity: 0.55,
    },
    {
        id: "text-embedding-3-small",
        dim: 768,
        runs: ["hosted"],
        prefixes: { document: "", query: "" },
        hosted: { provider: "openai", model: "text-embedding-3-small", dimensions: 768 },
        minSimilarity: 0.3,
    },
];
export const DEFAULT_EMBEDDING_MODEL = EMBEDDING_MODELS[0].id;
export const embeddingModelById = (id) => EMBEDDING_MODELS.find((m) => m.id === id);
export const MEMORY_EMBEDDING_ENCODING = "f16";
/** A vector on the wire: little-endian IEEE 754 half floats, L2-normalised before quantising, base64 (dim * 2 bytes before encoding). */
export const memoryEmbeddingSchema = z.object({
    model: z.string().max(80),
    dim: z.number().int().min(64).max(4000),
    encoding: z.literal(MEMORY_EMBEDDING_ENCODING),
    values: z.string().max(10_700),
    /** embeddingTextDigest() of the text the vector was made from. */
    textDigest: z.string().regex(/^[0-9a-f]{32}$/),
});
/** A question's vector: as an item's, without the digest. */
export const memoryQueryEmbeddingSchema = memoryEmbeddingSchema.omit({ textDigest: true });
/** The floor for a model that names none (`minSimilarity` on the catalogue entry): below it an item never enters an answer by meaning alone. */
export const MEMORY_MEANING_MIN_SIMILARITY = 0.35;
/** The meaning floor for a model: its own, else the fallback. */
export const meaningFloor = (model) => model?.minSimilarity ?? MEMORY_MEANING_MIN_SIMILARITY;
/** Reciprocal rank fusion's constant: score = Σ 1 / (k + rank) over the lists an item is in. */
export const MEMORY_FUSION_K = 60;
export const memoryItemInputSchema = z.object({
    id: idSchema,
    kind: memoryItemKindSchema,
    rootId: rootIdSchema.optional(),
    scope: memoryScopeSchema.default("repo"),
    label: memoryItemLabelInputSchema.optional(),
    data: z.record(z.string(), z.unknown()),
    /** Sent only when the home has not acknowledged this text's digest; an upsert without one keeps the stored vector while the text is unchanged. */
    embedding: memoryEmbeddingSchema.optional(),
});
export const MEMORY_DELTA_MAX_UPSERTS = 2000;
/** The most bytes one push may carry (the route's own body limit): a batch past it is answered 413 in words, and the host sends fewer items. */
export const MEMORY_DELTA_MAX_BYTES = 16 * 1024 * 1024;
export const memoryDeltaSchema = z.object({
    rootId: rootIdSchema,
    revision: z.string().min(1).max(200),
    /** For the organisation's index: where the items came from ("handbook", "standards", "adr", a repository). */
    source: z.string().min(1).max(200).optional(),
    /** This revision replaces the repository's items; `done` says the last call of it has arrived. */
    full: z.boolean().default(false),
    done: z.boolean().default(false),
    upserts: z.array(memoryItemInputSchema).max(MEMORY_DELTA_MAX_UPSERTS).default([]),
    removes: z.array(idSchema).max(5000).default([]),
});
// ---- The past (M3, D328) ---------------------------------------------------------------------------------------
/** A point in the memory's history: a sequence number, a repository's extraction (its graph hash), or a time (the newest revision at or before it). */
export const memoryAsOfSchema = z.union([
    z.object({ seq: z.number().int().nonnegative() }),
    z.object({ revision: z.string().min(1).max(200), rootId: rootIdSchema }),
    z.object({ at: z.string().datetime({ offset: true }) }),
]);
/**
 * `asOf` on the GET routes (`graph`, `items`): `asOfSeq`, or `asOfRevision` with `rootId`, or `asOfAt`. Undefined
 * when none is given; a malformed one is a 400 like any bad query.
 */
export function memoryAsOfFromQuery(q) {
    const str = (k) => (typeof q[k] === "string" && q[k].length > 0 ? q[k] : undefined);
    const seq = str("asOfSeq");
    const revision = str("asOfRevision");
    const at = str("asOfAt");
    if ([seq, revision, at].filter((v) => v !== undefined).length > 1)
        return { error: "name one point: asOfSeq, asOfRevision or asOfAt" };
    const candidate = seq !== undefined ? { seq: Number(seq) } : revision !== undefined ? { revision, rootId: str("rootId") ?? "" } : at !== undefined ? { at } : undefined;
    if (!candidate)
        return {};
    const parsed = memoryAsOfSchema.safeParse(candidate);
    return parsed.success ? { asOf: parsed.data } : { error: "asOf is not a sequence number, a revision with its rootId, or an ISO time" };
}
export const memoryQuerySchema = z.object({
    question: z.string().min(1).max(2000),
    scope: z.enum(["repo", "knowledge", "all"]).default("all"),
    rootId: rootIdSchema.optional(),
    limit: z.number().int().positive().max(100).default(20),
    /** On the organisation's index: also search every project the person can read; answers name the project. */
    projects: z.enum(["mine"]).optional(),
    /** The question's vector with the index's model; another model's is answered by words, with the reason. */
    embedding: memoryQueryEmbeddingSchema.optional(),
    /** Read the memory as it stood then (M3): by words only. */
    asOf: memoryAsOfSchema.optional(),
});
/** `POST /projects/v1/:id/memory/promote`, owners: copy readable items into the organisation's index. */
export const memoryPromoteSchema = z
    .object({
    itemIds: z.array(idSchema).max(5000).optional(),
    all: z.boolean().optional(),
    /** Raise the label on the way; the index never lowers one. */
    label: z.object({ sensitivity: sensitivitySchema.optional(), compartments: z.array(compartmentIdSchema).max(64).default([]) }).optional(),
})
    .refine((p) => p.all || (p.itemIds && p.itemIds.length > 0), { message: "name the items, or all" });
/** `PUT /projects/v1/:id/memory/mirror`, owners: keep the project's memory mirrored into the organisation's index. */
export const memoryMirrorSchema = z.object({
    enabled: z.boolean(),
    scope: z.enum(["knowledge", "all"]).default("knowledge"),
    /** When the project's existing items were last copied across (the server's word; a request's is ignored). Unset: the job still has to. */
    caughtUpAt: z.string().optional(),
});
/** An item's id in the organisation's index when it came from a project. */
export const promotedItemId = (projectId, id) => `${projectId}:${id}`;
export const memoryExplainSchema = z.object({
    concept: z.string().min(1).max(500),
    scope: z.enum(["repo", "knowledge", "all"]).default("all"),
    rootId: rootIdSchema.optional(),
    asOf: memoryAsOfSchema.optional(),
});
export const memoryPathSchema = z.object({
    from: z.string().min(1).max(500),
    to: z.string().min(1).max(500),
    scope: z.enum(["repo", "knowledge", "all"]).default("all"),
    asOf: memoryAsOfSchema.optional(),
});
export const MEMORY_PATH_MAX_HOPS = 12;
export const memoryTextDataSchema = z.object({
    title: z.string().min(1).max(500),
    text: z.string().max(100_000),
    tags: z.array(z.string().min(1).max(100)).max(50).default([]),
});
/** `POST …/memory/items`: what a person or agent writes back from a session. */
export const memoryWriteSchema = z.object({
    kind: z.enum(["note", "lesson", "decision"]),
    rootId: rootIdSchema.optional(),
    /**
     * The same key written again, for the same kind and repository, replaces the item instead of
     * adding one: a repository's lessons file kept as one lesson. Its label is never lowered by a
     * replace. Without a key every write is a new item.
     */
    key: z.string().regex(/^[A-Za-z0-9._:/-]{1,100}$/).optional(),
    data: memoryTextDataSchema,
    label: z.object({ sensitivity: sensitivitySchema.optional(), compartments: z.array(compartmentIdSchema).max(64).default([]) }).optional(),
    embedding: memoryEmbeddingSchema.optional(),
});
/** The session's high-water mark: the highest sensitivity it has read, sent by the workbench. */
export const MEMORY_HWM_HEADER = "x-aioe-label-hwm";
/** The compartments the writing session has read (comma-separated): a write-back carries them all, so nothing read under a compartment leaves it. */
export const MEMORY_COMPARTMENTS_HEADER = "x-aioe-label-compartments";
export const memoryRelabelSchema = z.object({
    label: z.object({ sensitivity: sensitivitySchema, compartments: z.array(compartmentIdSchema).max(64).default([]) }),
    reason: z.string().min(1).max(2000),
});
/** Problems with an item's `data` for its kind, as sentences; empty when fine. */
export function memoryDataProblems(kind, data) {
    const str = (k) => typeof data[k] === "string" && data[k].length > 0;
    const out = [];
    switch (kind) {
        case "node":
            if (!str("name"))
                out.push("a node has a name");
            if (!str("type"))
                out.push("a node has a type");
            break;
        case "edge":
            if (!str("from") || !str("to"))
                out.push("an edge has from and to");
            if (!str("relation"))
                out.push("an edge has a relation");
            break;
        case "community":
            if (!str("name"))
                out.push("a community has a name");
            if (!Array.isArray(data.members))
                out.push("a community lists its members");
            break;
        case "note":
        case "lesson":
        case "decision":
            if (!str("title"))
                out.push(`a ${kind} has a title`);
            if (typeof data.text !== "string")
                out.push(`a ${kind} has text`);
            break;
        case "artifact":
            if (!str("name"))
                out.push("an artifact has a name");
            break;
        case "knowledge":
            // Distilled knowledge (memoryKnowledge.ts): its type, key, title and text are checked there.
            if (!str("type") || !str("key") || !str("title"))
                out.push("a knowledge item has a type, a key and a title");
            if (typeof data.text !== "string")
                out.push("a knowledge item has text");
            break;
    }
    return out;
}
/** The name a person sees for an item. */
export function memoryItemName(item) {
    const d = item.data;
    const s = (k) => (typeof d[k] === "string" ? d[k] : undefined);
    return s("name") ?? s("title") ?? (item.kind === "edge" ? `${s("from") ?? "?"} ${s("relation") ?? "->"} ${s("to") ?? "?"}` : item.id);
}
/** The words an item is found by: its name, type, summary, text, tags and relation. */
export function memoryItemText(item) {
    const d = item.data;
    const parts = [memoryItemName(item)];
    for (const k of ["type", "summary", "text", "why", "relation", "sourceFile"])
        if (typeof d[k] === "string")
            parts.push(d[k]);
    if (Array.isArray(d.terms))
        parts.push(d.terms.filter((t) => typeof t === "string").join(" "));
    if (Array.isArray(d.tags))
        parts.push(d.tags.filter((t) => typeof t === "string").join(" "));
    return parts.join("\n");
}
// ---- What is embedded, and the digests both sides compute ------------------------------------------------------
/** About 2,000 tokens: an item's text is cut to this many characters, at a sentence where there is one. */
export const MEMORY_EMBEDDING_MAX_CHARS = 8000;
/** Whether an item is worth a vector: never an edge; a repository node only with a summary; everything else always. */
export function memoryItemEmbeddable(item) {
    if (item.kind === "edge")
        return false;
    if (item.kind === "node" && item.scope === "repo")
        return typeof item.data.summary === "string" && item.data.summary.trim().length > 0;
    return true;
}
/** The text a vector is made from, without the model's prefix: memoryItemText(), cut at a sentence. */
export function memoryEmbeddingSource(item) {
    const text = memoryItemText(item);
    if (text.length <= MEMORY_EMBEDDING_MAX_CHARS)
        return text;
    const head = text.slice(0, MEMORY_EMBEDDING_MAX_CHARS);
    const end = Math.max(head.lastIndexOf(". "), head.lastIndexOf(".\n"), head.lastIndexOf("\n\n"));
    return end > MEMORY_EMBEDDING_MAX_CHARS / 2 ? head.slice(0, end + 1) : head;
}
/** What the model is given for a document: its prefix, then the source text. */
export function memoryEmbeddingText(item, model) {
    return model.prefixes.document + memoryEmbeddingSource(item);
}
/** What the model is given for a question. */
export function memoryQueryEmbeddingText(question, model) {
    return model.prefixes.query + question;
}
/** SHA-256 of the source text (no prefix), first 32 hex: whether a stored vector still matches its item. */
export function embeddingTextDigest(source) {
    return sha256Hex(source).slice(0, 32);
}
/**
 * Whether an item's content changed (M3): SHA-256 of the canonical JSON of its kind, scope, label (sensitivity,
 * compartments as a set, pinned) and data, and the text digest of its vector. Leaves out the provenance, whose
 * `at` and `commit` move on every push for every item: an upsert with the same digest is not a change.
 */
export function memoryContentDigest(item, textDigest) {
    const label = { sensitivity: item.label.sensitivity, compartments: [...new Set(item.label.compartments ?? [])].sort(), pinned: item.label.pinned === true };
    return sha256Hex(memoryCanonicalJson({ kind: item.kind, scope: item.scope, label, data: item.data, textDigest: textDigest ?? null }));
}
/** JSON with object keys sorted at every depth and undefined members left out, so the same content is the same bytes everywhere. */
export function memoryCanonicalJson(value) {
    if (value === null || typeof value !== "object")
        return JSON.stringify(value) ?? "null";
    if (Array.isArray(value))
        return `[${value.map((v) => (v === undefined ? "null" : memoryCanonicalJson(v))).join(",")}]`;
    const obj = value;
    const keys = Object.keys(obj).filter((k) => obj[k] !== undefined).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${memoryCanonicalJson(obj[k])}`).join(",")}}`;
}
// ---- Half floats on the wire -----------------------------------------------------------------------------------
/** L2-normalises `values`, quantises them to half floats (little-endian) and base64s them. */
export function encodeMemoryEmbedding(values) {
    let norm = 0;
    for (let i = 0; i < values.length; i++)
        norm += values[i] * values[i];
    norm = Math.sqrt(norm) || 1;
    const bytes = new Uint8Array(values.length * 2);
    for (let i = 0; i < values.length; i++) {
        const h = toHalf(values[i] / norm);
        bytes[i * 2] = h & 0xff;
        bytes[i * 2 + 1] = h >> 8;
    }
    let binary = "";
    for (let i = 0; i < bytes.length; i += 0x8000)
        binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    return btoa(binary);
}
/** The vector back as 32-bit floats; undefined when the text is not `dim` half floats. */
export function decodeMemoryEmbedding(values, dim) {
    let binary;
    try {
        binary = atob(values);
    }
    catch {
        return undefined;
    }
    if (binary.length !== dim * 2)
        return undefined;
    const out = new Float32Array(dim);
    for (let i = 0; i < dim; i++)
        out[i] = fromHalf(binary.charCodeAt(i * 2) | (binary.charCodeAt(i * 2 + 1) << 8));
    return out;
}
const f32 = new Float32Array(1);
const u32 = new Uint32Array(f32.buffer);
function toHalf(v) {
    f32[0] = v;
    const x = u32[0];
    const sign = (x >>> 16) & 0x8000;
    const exp = (x >>> 23) & 0xff;
    let mant = x & 0x7fffff;
    if (exp === 0xff)
        return sign | 0x7c00 | (mant ? 0x200 : 0);
    const e = exp - 127 + 15;
    if (e >= 0x1f)
        return sign | 0x7c00;
    if (e <= 0) {
        if (e < -10)
            return sign;
        mant |= 0x800000;
        const shift = 14 - e;
        let h = mant >> shift;
        if ((mant >> (shift - 1)) & 1 && (mant & ((1 << (shift - 1)) - 1) || h & 1))
            h++;
        return sign | h;
    }
    let h = sign | (e << 10) | (mant >> 13);
    // Round to nearest, ties to even.
    if (mant & 0x1000 && (mant & 0xfff || h & 1))
        h++;
    return h;
}
function fromHalf(h) {
    const sign = h & 0x8000 ? -1 : 1;
    const exp = (h >> 10) & 0x1f;
    const mant = h & 0x3ff;
    if (exp === 0)
        return sign * mant * 2 ** -24;
    if (exp === 0x1f)
        return mant ? NaN : sign * Infinity;
    return sign * (1 + mant / 1024) * 2 ** (exp - 15);
}
// SHA-256 in plain TypeScript: this file runs in the API, the console, the phone and the workbench's renderer, and
// `node:crypto` is not in all of them. Checked against node:crypto in projectMemory.test.ts.
const K256 = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);
function sha256Hex(text) {
    const data = new TextEncoder().encode(text);
    const bitLength = data.length * 8;
    const padded = new Uint8Array(((data.length + 9 + 63) >> 6) << 6);
    padded.set(data);
    padded[data.length] = 0x80;
    const view = new DataView(padded.buffer);
    view.setUint32(padded.length - 8, Math.floor(bitLength / 2 ** 32));
    view.setUint32(padded.length - 4, bitLength >>> 0);
    const h = new Uint32Array([0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19]);
    const w = new Uint32Array(64);
    const rotr = (x, n) => (x >>> n) | (x << (32 - n));
    for (let off = 0; off < padded.length; off += 64) {
        for (let i = 0; i < 16; i++)
            w[i] = view.getUint32(off + i * 4);
        for (let i = 16; i < 64; i++) {
            const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
            const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
            w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
        }
        let [a, b, c, d, e, f, g, hh] = [h[0], h[1], h[2], h[3], h[4], h[5], h[6], h[7]];
        for (let i = 0; i < 64; i++) {
            const t1 = (hh + (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) + ((e & f) ^ (~e & g)) + K256[i] + w[i]) >>> 0;
            const t2 = ((rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) >>> 0;
            hh = g;
            g = f;
            f = e;
            e = (d + t1) >>> 0;
            d = c;
            c = b;
            b = a;
            a = (t1 + t2) >>> 0;
        }
        h[0] = (h[0] + a) >>> 0;
        h[1] = (h[1] + b) >>> 0;
        h[2] = (h[2] + c) >>> 0;
        h[3] = (h[3] + d) >>> 0;
        h[4] = (h[4] + e) >>> 0;
        h[5] = (h[5] + f) >>> 0;
        h[6] = (h[6] + g) >>> 0;
        h[7] = (h[7] + hh) >>> 0;
    }
    return [...h].map((x) => x.toString(16).padStart(8, "0")).join("");
}
// ---- How an answer was found (M1) --------------------------------------------------------------------------------
/** On a query's result: whether meaning was used, and when not, why. A different model is never an error. */
export const memoryRetrievalSchema = z.object({
    words: z.literal(true),
    meaning: z.boolean(),
    /** The index has no model, the question's model differs, the asker sent no vector, it is off, or the question reads the past. */
    reason: z.enum(["index-has-none", "model-mismatch", "no-embedding", "off", "as-of"]).optional(),
    /** The index's model, so the asker can embed with it and ask again. */
    model: z.string().optional(),
});
/** `POST …/memory/embeddings`: vectors for items listed missing; one whose text changed since is answered stale and dropped. */
export const memoryEmbeddingsPostSchema = z.object({
    model: z.string().max(80),
    vectors: z.array(z.object({ id: idSchema, embedding: memoryEmbeddingSchema })).min(1).max(500),
});
// ---- Revisions (M3) ----------------------------------------------------------------------------------------------
export const MEMORY_REVISION_KINDS = ["extraction", "write", "proposal", "relabel", "removal", "promotion", "mirror", "embedding"];
const kindCounts = z.partialRecord(memoryItemKindSchema, z.number().int().nonnegative());
/** One change to an index, readable later. An extraction of many calls is one revision, at its `done` call's seq. */
export const memoryRevisionSchema = z.object({
    seq: z.number().int().nonnegative(),
    at: z.string(),
    kind: z.enum(MEMORY_REVISION_KINDS),
    /** "" for project-level. */
    rootId: rootIdSchema,
    /** The extraction's graph hash, for kind "extraction". */
    revision: z.string().max(200).optional(),
    commit: z.string().max(100).optional(),
    by: z.object({ principal: z.string().optional(), actor: z.string().optional(), name: z.string().optional(), device: z.string().optional() }),
    /** Per item kind: what came, what changed, what went. Only what the reader may read is counted. */
    counts: z.object({ added: kindCounts, changed: kindCounts, removed: kindCounts }),
    proposalId: z.string().optional(),
});
/** How long history is kept when the policy says nothing (`memory.history.keepDays`). Every revision is kept 30 days whatever it says. */
export const MEMORY_HISTORY_KEEP_DAYS_DEFAULT = 365;
export const MEMORY_HISTORY_EVERY_REVISION_DAYS = 30;
// ---- Proposals (M2, D327) ----------------------------------------------------------------------------------------
export const MEMORY_PROPOSAL_STATES = ["proposed", "accepted", "rejected", "stale", "expired"];
/** Undecided proposals expire after this many days; rejected ones are removed this many days after the decision. */
export const MEMORY_PROPOSAL_EXPIRE_DAYS = 14;
export const MEMORY_PROPOSAL_REJECTED_KEEP_DAYS = 30;
export const MEMORY_WRITES_MODES = ["direct", "review", "reviewByManager"];
export const memoryProposerSchema = z.object({
    sessionId: z.string().max(200),
    /** The agent or team member, in words for the Inbox: "Rey (Coder)". */
    actor: z.string().max(200),
    /**
     * The agent's principal (D410, AIOE roles step 6; shared/rbac/agentPrincipal.ts), when the machine named one in
     * `x-aioe-actor`: what "its own proposal" is decided by. The home sets it; `actor` stays the name a person reads.
     */
    actorPrincipal: z.string().max(200).optional(),
    memberId: z.string().max(100).optional(),
    teamId: z.string().max(100).optional(),
    teamName: z.string().max(200).optional(),
    taskId: z.string().max(100).optional(),
    /** "T-12" */
    taskRef: z.string().max(100).optional(),
    /** The person the work is for. The home takes it from the token, never from the body. */
    principal: z.string().max(200).optional(),
    /** The desktop or node it ran on. */
    device: z.string().max(200).optional(),
});
export const memoryProposalItemSchema = memoryWriteSchema.extend({
    /** Its id within the proposal, so a reviewer can take some items and not others. */
    id: z.string().min(1).max(100).optional(),
    /** For a keyed write: the version of the item it would replace, as the proposer read it (0 = a new item). */
    baseVersion: z.number().int().nonnegative().optional(),
    state: z.enum(["proposed", "accepted", "rejected", "stale"]).default("proposed"),
    landedId: z.string().optional(),
    landedVersion: z.number().int().positive().optional(),
});
export const memoryProposalSchema = z.object({
    /** "mp_…" */
    id: z.string().min(1).max(100),
    /** "" for the organisation's own index. */
    projectId: z.string(),
    /** `${sessionId}:${taskId ?? "-"}`: later writes of the same run join the proposal while it waits. */
    batchKey: z.string().max(300),
    proposer: memoryProposerSchema,
    items: z.array(memoryProposalItemSchema).min(1).max(200),
    /** Who should decide: the team's operations manager, or a person. */
    reviewBy: z.enum(["manager", "person"]),
    state: z.enum(MEMORY_PROPOSAL_STATES),
    /** The highest label of its items: the proposal is shown only to someone who may read that. */
    label: z.object({ sensitivity: sensitivitySchema, compartments: z.array(compartmentIdSchema).max(64) }),
    proposedAt: z.string(),
    updatedAt: z.string(),
    decidedBy: z.object({ principal: z.string().optional(), actor: z.string().optional(), name: z.string().optional() }).optional(),
    decidedAt: z.string().optional(),
    reason: z.string().max(2000).optional(),
    /** The revision the accepted items landed in. */
    landedSeq: z.number().int().nonnegative().optional(),
    expiresAt: z.string(),
});
/** `POST …/memory/proposals` → 201 { proposal }, or 200 { proposal } when it joined one waiting under the same batchKey. */
export const memoryProposalCreateSchema = z.object({
    batchKey: z.string().min(1).max(300),
    proposer: memoryProposerSchema,
    items: z.array(memoryProposalItemSchema).min(1).max(200),
    /** "person" when the proposer's team has no operations manager to review it (only the host knows); a home never sends a person's review to a manager. */
    reviewBy: z.literal("person").optional(),
});
/** `POST …/memory/proposals/:pid/escalate`: the operations manager answers "a person should look" → { proposal }. */
export const memoryProposalEscalateSchema = z.object({ reason: z.string().max(2000).optional() });
/** The Inbox action that decides a proposal (params { proposalId, decision: "accepted" | "rejected", itemIds? }; a reason in the body). */
export const MEMORY_PROPOSAL_DECIDE_RPC = "aioe.memory.proposal.decide";
export const memoryProposalDedupeKey = (proposalId) => `memory-proposal:${proposalId}`;
export const memoryReviewedDedupeKey = (proposalId) => `memory-reviewed:${proposalId}`;
/** "3 lessons and 1 decision from Rey on task T-12": what a proposal holds, in words. */
export function memoryProposalTitle(p) {
    const counts = new Map();
    for (const i of p.items)
        counts.set(i.kind, (counts.get(i.kind) ?? 0) + 1);
    const words = [...counts].map(([kind, n]) => `${n} ${n === 1 ? kind : `${kind}s`}`);
    const what = words.length > 1 ? `${words.slice(0, -1).join(", ")} and ${words.at(-1)}` : (words[0] ?? "nothing");
    const who = p.proposer.actor.replace(/\s*\(.*\)\s*$/, "") || "an agent";
    const task = p.proposer.taskRef ?? p.proposer.taskId;
    return `${what} from ${who}${task ? ` on task ${task}` : ""}`;
}
/** `POST …/memory/proposals/:pid/accept` → { proposal, landed: [{ id, itemId, version, seq }], stale: [id] }; without itemIds, all. */
export const memoryProposalAcceptSchema = z.object({
    itemIds: z.array(z.string().min(1).max(100)).max(200).optional(),
    reason: z.string().max(2000).optional(),
});
/** `POST …/memory/proposals/:pid/reject` → { proposal } */
export const memoryProposalRejectSchema = z.object({
    reason: z.string().min(1).max(2000),
    itemIds: z.array(z.string().min(1).max(100)).max(200).optional(),
});
// ---- What a home offers (additive on `…/memory/status`) --------------------------------------------------------
export const memoryCapabilitiesSchema = z.object({
    /** The index's model, its dimensions, the embeddable items and how many carry the current model. */
    embeddings: z.object({ model: z.string(), dim: z.number().int(), items: z.number().int().nonnegative(), current: z.number().int().nonnegative() }).optional(),
    revisions: z.object({ keepDays: z.number().int().nonnegative() }).optional(),
    proposals: z.literal(true).optional(),
});
//# sourceMappingURL=projectMemory.js.map